import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionSummary, Box, Button, Popover, Stack, styled, Typography } from "@mui/material";
import { useActionHook, useModals } from "hooks";
import {
  IApiServiceError,
  useQuickViewMetaSelector,
  useQuickViewQueriesSelect,
  useReportsActions,
  useUserActions
} from "store";
import { IGetQuickViewData, QuickViewQueries } from "store/types";

import { AccordionMeasureItem } from "./AccordionMeasureItem";
import { AccordionSimpleItem } from "./AccordionSimpleItem";

interface Props {
  onCloseWithRefetch: (data?: Partial<IGetQuickViewData>) => void;
  setCustomMenuOpen: Dispatch<SetStateAction<HTMLElement | null>>;
  customMenuOpen: HTMLElement | null;
  container?: Element | null;
}

const StyledAccordion = styled(Accordion)(() => ({
  "&.Mui-expanded": { margin: "0 !important" },
  "&.MuiAccordion-root:before": {
    background: "transparent"
  },
  "&.MuiAccordion-root": {
    boxShadow: "none",
    borderRadius: "0 !important",
    "&:first-of-type": {
      borderRadius: "0 !important"
    },
    "&:last-of-type": {
      borderRadius: "0 !important"
    },
    "&:not(:last-child)": {
      borderBottom: "none"
    }
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  fontSize: "16px",
  fontWeight: "500",
  "&.MuiAccordionSummary-root": { display: "flex", flexDirection: "row-reverse", gap: "12px" }
}));

const SYSTEM_COLUMNS = [
  { key: "investor", title: "Investor" },
  { key: "round", title: "Round" },
  { key: "last_update", title: "Last Update" }
];

export const SmartSheetSettings: React.FC<Props> = ({
  onCloseWithRefetch,
  setCustomMenuOpen,
  customMenuOpen,
  container
}) => {
  const { columns } = useQuickViewQueriesSelect();
  const { characteristics, calculatedFields, measures } = useQuickViewMetaSelector();

  const { setQuickViewQueriesAction } = useReportsActions();
  const { updateQuickViewSystemColsAction } = useUserActions();
  const { errorModal } = useModals();

  const { action: updateSystemCols } = useActionHook(updateQuickViewSystemColsAction, {
    onReject: (e) => {
      const err = e as IApiServiceError;
      const msg =
        Object.values(err.errors ?? {})
          .flat()
          .join(", ") || "Something when wrong";
      errorModal({ msg });
    }
  });

  const [localState, setLocalState] = useState<QuickViewQueries["columns"]>(columns);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [scrollStart, setScrollStart] = useState(false);

  useEffect(() => {
    setLocalState(columns);
  }, [columns]);

  // const stateColumnsCopy = useMemo(() => state.columns, []);
  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleClickSimpleItem = ({ field, value }: { field: keyof QuickViewQueries["columns"]; value: string }) => {
    setLocalState((prevState) => {
      const isIncludeValue = prevState[field].includes(value);
      return {
        ...prevState,
        [field]: isIncludeValue ? prevState[field].filter((item) => item !== value) : [...prevState[field], value]
      };
    });
  };

  const handleToggleSystemColumn = (columnKey: string) => {
    setLocalState((prevState) => {
      const currentVisible = prevState.visibleSystemColumns;
      const isVisible = currentVisible.includes(columnKey);
      return {
        ...prevState,
        visibleSystemColumns: isVisible
          ? currentVisible.filter((key) => key !== columnKey)
          : [...currentVisible, columnKey]
      };
    });
  };

  const closePopupAfterScroll = () => {
    if (scrollStart) {
      setScrollStart(false);
      return;
    }
    setScrollStart(true);
  };

  const onClose = () => {
    // setState((prev) => {
    //   return {
    //     ...prev
    //     // columns: stateColumnsCopy
    //   };
    // });
    setCustomMenuOpen(null);
  };

  const onApply = () => {
    let updatedColumnsOrder = localState.columnsOrder;

    if (updatedColumnsOrder.length > 0) {
      // Remove system columns that are now hidden
      updatedColumnsOrder = updatedColumnsOrder.filter(
        (key) => !SYSTEM_COLUMNS.some((sc) => sc.key === key && !localState.visibleSystemColumns.includes(sc.key))
      );

      // Add system columns that were re-enabled and are missing from columnsOrder
      const missingSystemCols = localState.visibleSystemColumns.filter(
        (key) => SYSTEM_COLUMNS.some((sc) => sc.key === key) && !updatedColumnsOrder.includes(key)
      );
      // Insert them after "deal" (index 1) to keep deal first
      if (missingSystemCols.length > 0) {
        const dealIdx = updatedColumnsOrder.indexOf("deal");
        const insertAt = dealIdx >= 0 ? dealIdx + 1 : 0;
        updatedColumnsOrder = [
          ...updatedColumnsOrder.slice(0, insertAt),
          ...missingSystemCols,
          ...updatedColumnsOrder.slice(insertAt)
        ];
      }
    }

    const updatedLocalState = { ...localState, columnsOrder: updatedColumnsOrder };

    setQuickViewQueriesAction({ queries: { columns: updatedLocalState } });
    updateSystemCols(updatedLocalState.visibleSystemColumns);
    onCloseWithRefetch({
      calculated_fields: localState.calculatedFields,
      characteristics: localState.characteristics,
      measure_ids: localState.measures
    });
    setCustomMenuOpen(null);
  };

  const setMeasure = useCallback<(cb: (prevState: string[]) => string[]) => void>(
    (cb) => {
      setLocalState((prevState) => ({ ...prevState, measures: cb(prevState.measures) }));
    },
    [setLocalState]
  );

  return (
    <Popover
      open={!!customMenuOpen}
      anchorEl={customMenuOpen}
      onClose={onClose}
      container={container ?? document.body}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right"
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right"
      }}
      sx={{
        "& .MuiPaper-root": {
          width: "230px",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow:
            "0px 3px 3px -2px rgba(38, 54, 70, 0.16), 0px 3px 4px 0px rgba(38, 54, 70, 0.10), 0px 1px 8px 0px rgba(38, 54, 70, 0.08)"
        }
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "600",
            lineHeight: "16px",
            background: "#fff",
            padding: "10px 10px 10px 20px"
          }}
        >
          Set Column
        </Typography>
      </Box>
      <StyledAccordion onChange={handleChange("SystemColumns")} expanded={expanded === "SystemColumns"}>
        <StyledAccordionSummary
          {...(expanded === "SystemColumns" && { sx: { background: "#F6F8FC" } })}
          expandIcon={<ExpandMoreIcon sx={{ fill: "black" }} />}
        >
          System Columns
        </StyledAccordionSummary>
        <Stack sx={{ maxHeight: "250px", overflow: "auto" }}>
          {SYSTEM_COLUMNS.map((column) => (
            <AccordionSimpleItem
              key={column.key}
              item={column.title}
              isActive={(localState.visibleSystemColumns ?? ["investor", "round", "last_update"]).includes(column.key)}
              onClick={() => handleToggleSystemColumn(column.key)}
            />
          ))}
        </Stack>
      </StyledAccordion>
      <StyledAccordion onChange={handleChange("Measures")} expanded={expanded === "Measures"}>
        <StyledAccordionSummary
          {...(expanded === "Measures" && { sx: { background: "#F6F8FC" } })}
          expandIcon={<ExpandMoreIcon sx={{ fill: "black" }} />}
        >
          Measurements
        </StyledAccordionSummary>
        <Stack onScroll={closePopupAfterScroll} sx={{ height: "250px", overflow: "scroll" }}>
          {measures?.map((item, idx) => (
            <AccordionMeasureItem
              key={idx}
              item={item}
              scroll={scrollStart}
              selectedItems={localState.measures}
              setMeasure={setMeasure}
              container={container}
            />
          ))}
        </Stack>
      </StyledAccordion>
      <StyledAccordion onChange={handleChange("Calculated")} expanded={expanded === "Calculated"}>
        <StyledAccordionSummary
          {...(expanded === "Calculated" && { sx: { background: "#F6F8FC" } })}
          expandIcon={<ExpandMoreIcon sx={{ fill: "black" }} />}
        >
          Calculated
        </StyledAccordionSummary>
        <Stack sx={{ height: "250px", overflow: "scroll" }}>
          {calculatedFields?.map((item, idx) => (
            <AccordionSimpleItem
              key={idx}
              item={item}
              isActive={localState.calculatedFields.includes(item)}
              onClick={() => handleClickSimpleItem({ field: "calculatedFields", value: item })}
            />
          ))}
        </Stack>
      </StyledAccordion>
      <StyledAccordion onChange={handleChange("Characteristics")} expanded={expanded === "Characteristics"}>
        <StyledAccordionSummary
          {...(expanded === "Characteristics" && { sx: { background: "#F6F8FC" } })}
          expandIcon={<ExpandMoreIcon sx={{ fill: "black" }} />}
        >
          Characteristics
        </StyledAccordionSummary>
        <Stack onScroll={closePopupAfterScroll} sx={{ height: "250px", overflow: "scroll" }}>
          {characteristics?.map((item, idx) => (
            <AccordionSimpleItem
              key={idx}
              item={item}
              isActive={localState.characteristics.includes(item)}
              onClick={() => handleClickSimpleItem({ field: "characteristics", value: item })}
            />
          ))}
        </Stack>
      </StyledAccordion>
      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        gap="10px"
        sx={{ padding: "10px", background: "#fff" }}
      >
        <Button size="sm" variant="outlined" sx={{ minWidth: "53px" }} onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" variant="contained" sx={{ backgroundColor: "#071F35", minWidth: "46px" }} onClick={onApply}>
          Apply
        </Button>
      </Stack>
    </Popover>
  );
};
